import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { PatientLabService } from '../../../core/services/patient-lab.service';
import { PatientService } from '../../../core/services/patient.service';
import * as moment from 'moment';

export interface LabMonitoringDialogData {
  patient: any;
  labOrders: any[];
}

interface MonitoringData {
  testName: string;
  testCode: string;
  unit: string;
  referenceRange: string;
  latestValue: number | string;
  latestDate: string;
  trend: 'increasing' | 'decreasing' | 'stable' | 'no_data';
  trendPercentage: number;
  status: 'normal' | 'high' | 'low' | 'critical' | 'abnormal' | 'no_data';
  history: Array<{
    value: number | string;
    date: string;
    status: string;
    comment?: string;
  }>;
  alerts: Array<{
    type: 'critical' | 'warning' | 'info';
    message: string;
    date: string;
  }>;
  isMicrobiology?: boolean; // Flag to indicate if this is a microbiology test
  latestComment?: string; // Latest comment for this test
}

@Component({
  selector: 'app-lab-monitoring-dialog',
  templateUrl: './lab-monitoring-dialog.component.html',
  styleUrls: ['./lab-monitoring-dialog.component.scss']
})
export class LabMonitoringDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  patient: any;
  completedLabOrders: any[] = [];
  monitoringData: MonitoringData[] = [];
  isLoading = true;
  searchQuery = '';
  viewMode: 'grid' | 'table' | 'compare' = 'grid';
  
  // Date range filtering (like statistics)
  selectedDateRange: 'today' | 'week' | 'month' | 'year' | 'custom' = 'month';
  customStartDate: moment.Moment | null = null;
  customEndDate: moment.Moment | null = null;
  maxDate: moment.Moment = moment();
  
  // Date range chips
  dateRangeChips = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];
  
  // Pre-computed data for display
  filteredMonitoringData: MonitoringData[] = [];
  collectionDates: Date[] = [];
  
  // Pre-computed table data
  precomputedTableData: any[] = [];
  precomputedDateKeys: string[] = [];
  
  // Comparison mode data
  selectedForComparison: string[] = [];
  comparisonChartData: any = null;
  
  // Filtered data for comparison (only numeric/non-microbiology tests)
  get numericTestsForComparison(): MonitoringData[] {
    return this.filteredMonitoringData.filter(item => !item.isMicrobiology && this.hasNumericValues(item));
  }

  /**
   * Check if a test has numeric values that can be compared
   */
  private hasNumericValues(item: MonitoringData): boolean {
    // Check if latest value is numeric
    if (this.isNumericValue(item.latestValue)) {
      return true;
    }
    
    // Check if any history values are numeric
    return item.history.some(historyItem => this.isNumericValue(historyItem.value));
  }
  
  // Chart data for ngx-charts
  lineChartData: Array<{ name: string, series: Array<{ name: string, value: number }> }> = [];
  yAxisLabel = 'Value';
  
  // Color scheme for comparison chart
  comparisonColorScheme: Color = {
    domain: ['#009688', '#FF9800', '#009688', '#E91E63', '#9C27B0', '#FF5722', '#795548', '#607D8B'],
    group: ScaleType.Linear,
    selectable: false,
    name: 'Lab Comparison Chart'
  };

  // Format x-axis labels for comparison chart
  // Converts ISO date format (YYYY-MM-DD) to display format (MMM DD)
  formatComparisonXAxisLabel = (label: string): string => {
    const momentDate = moment.utc(label, 'YYYY-MM-DD', true);
    if (momentDate.isValid()) {
      return momentDate.format('MMM DD');
    }
    return label; // Fallback to original if not a valid date
  };

  // Trend tracking properties
  selectedTest: string | null = null;
  selectedTestData: MonitoringData | null = null;
  
  // Pre-computed data for selected test
  computedTrendData: any = null;
  
  // Chart data for trend analysis (ngx-charts line chart)
  trendLineChartData: Array<{ name: string, series: Array<{ name: string, value: number }> }> = [];
  
  // Color scheme for trend chart
  trendColorScheme: Color = {
    domain: ['#009688'],
    group: ScaleType.Linear,
    selectable: false,
    name: 'Lab Value Trend'
  };

  constructor(
    public dialogRef: MatDialogRef<LabMonitoringDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LabMonitoringDialogData,
    private patientLabService: PatientLabService,
    private patientService: PatientService,
    private cdr: ChangeDetectorRef
  ) {
    this.patient = data.patient;
  }

  ngOnInit(): void {
    this.loadMonitoringData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMonitoringData(): void {
    this.isLoading = true;
    
    // Subscribe to patient lab orders to get real-time updates
    this.patientLabService.patientLabOrders$
      .pipe(takeUntil(this.destroy$))
      .subscribe((labOrders: any[] | null) => {
        if (labOrders) {
          // Filter for completed orders with lab results
          this.completedLabOrders = labOrders.filter(order => {
            const hasLabResults = order.labResults && 
                                 order.labResults.specimens && 
                                 Array.isArray(order.labResults.specimens) && 
                                 order.labResults.specimens.length > 0;
            const isCompleted = ['completed', 'cancelled'].includes(order.status);
            return isCompleted && hasLabResults;
          });
          this.processMonitoringData();
        }
        this.isLoading = false;
      });
  }

  private processMonitoringData(): void {
    this.monitoringData = [];

    // Group results by test name and code
    const testGroups: { [key: string]: any[] } = {};

    this.completedLabOrders.forEach(order => {
      // Check if labResults exists and has the expected structure
      if (order.labResults && order.labResults.specimens && Array.isArray(order.labResults.specimens)) {
        order.labResults.specimens.forEach((specimen: any, specimenIndex: number) => {
          const isMicrobiology = specimen.type === 'MIC';
          
          if (specimen.procedures && Array.isArray(specimen.procedures)) {
            specimen.procedures.forEach((procedure: any, procedureIndex: number) => {
              if (procedure.tests && Array.isArray(procedure.tests)) {
                procedure.tests.forEach((test: any, testIndex: number) => {
                  if (test.testName && test.testCode) {
                    const key = `${test.testName}_${test.testCode}`;
                    if (!testGroups[key]) {
                      testGroups[key] = [];
                    }
                    // Use collectionDate (when specimen was collected) as it's the most clinically relevant date
                    // for lab monitoring and trending. Combine collectionDate and collectionTime for precision.
                    const collectionDate = specimen.collectionDate || order.orderDate;
                    const collectionTime = specimen.collectionTime || '00:00:00Z';
                    // Combine date and time: "2025-10-29" + "08:00:00Z" = "2025-10-29T08:00:00Z"
                    const fullCollectionDateTime = collectionTime ? `${collectionDate}T${collectionTime}` : collectionDate;
                    
                    testGroups[key].push({
                      testName: test.testName,
                      testCode: test.testCode,
                      value: test.value,
                      units: test.units,
                      range: test.range,
                      flag: test.flag,
                      comment: test.comment,
                      orderDate: order.orderDate,
                      resultDate: fullCollectionDateTime, // Use collection date/time for clinical accuracy
                      specimenIndex,
                      procedureIndex,
                      testIndex,
                      isMicrobiology // Track if this is a microbiology test
                    });
                  }
                });
              }
            });
          }
        });
      }
    });

    // Process each test group
    Object.values(testGroups).forEach(testResults => {
      if (testResults.length === 0) return;

      // Sort by date (most recent first)
      testResults.sort((a, b) => new Date(b.resultDate).getTime() - new Date(a.resultDate).getTime());

      const latestResult = testResults[0];
      const monitoringItem = this.createMonitoringItem(testResults);
      
      this.monitoringData.push(monitoringItem);

      // Add to test types if not already present
      const testType = this.getTestType(latestResult.testName);

      // Collect alerts
    });

    // Sort by test name
    this.monitoringData.sort((a, b) => a.testName.localeCompare(b.testName));
    
    this.applyFilters();
  }

  private createMonitoringItem(testResults: any[]): MonitoringData {
    const latestResult = testResults[0];
    const previousResult = testResults.length > 1 ? testResults[1] : null;
    const isMicrobiology = latestResult.isMicrobiology || false;

    // Calculate trend (only for numeric, non-microbiology values)
    let trend: 'increasing' | 'decreasing' | 'stable' | 'no_data' = 'no_data';
    let trendPercentage = 0;

    if (!isMicrobiology && previousResult && this.isNumericValue(latestResult.value) && this.isNumericValue(previousResult.value)) {
      const currentValue = parseFloat(latestResult.value);
      const previousValue = parseFloat(previousResult.value);
      const change = ((currentValue - previousValue) / previousValue) * 100;
      
      if (Math.abs(change) > 5) { // 5% threshold for significant change
        trend = change > 0 ? 'increasing' : 'decreasing';
        trendPercentage = Math.abs(change);
      } else {
        trend = 'stable';
      }
    }

    // Determine status (use microbiology-specific logic if needed)
    const status = isMicrobiology 
      ? this.determineMicrobiologyStatus(latestResult.value, latestResult.flag)
      : this.determineValueStatus(latestResult.value, latestResult.range);

    // Extract comment from latest result
    const latestComment = this.getTestCommentText(latestResult.comment);

    // Create history
    const history = testResults.map(result => ({
      value: result.value,
      date: result.resultDate,
      status: isMicrobiology
        ? this.determineMicrobiologyStatus(result.value, result.flag)
        : this.determineValueStatus(result.value, result.range),
      comment: this.getTestCommentText(result.comment)
    }));

    return {
      testName: latestResult.testName,
      testCode: latestResult.testCode,
      unit: latestResult.units || '',
      referenceRange: latestResult.range || 'N/A',
      latestValue: latestResult.value,
      latestDate: latestResult.resultDate,
      trend,
      trendPercentage,
      status,
      history,
      alerts: [],
      isMicrobiology,
      latestComment
    };
  }

  private isNumericValue(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(parseFloat(value));
  }

  private determineValueStatus(value: any, referenceRange: string): 'normal' | 'high' | 'low' | 'critical' | 'no_data' {
    if (!this.isNumericValue(value) || !referenceRange) {
      return 'no_data';
    }

    const numValue = parseFloat(value);
    
    // Parse reference range (assuming format like "3.5-5.0" or ">10" or "<2")
    const rangeMatch = referenceRange.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
    if (rangeMatch) {
      const min = parseFloat(rangeMatch[1]);
      const max = parseFloat(rangeMatch[2]);
      
      if (numValue < min) return 'low';
      if (numValue > max) return 'high';
      return 'normal';
    }

    // Handle single threshold ranges
    const highMatch = referenceRange.match(/>\s*(\d+\.?\d*)/);
    if (highMatch) {
      const threshold = parseFloat(highMatch[1]);
      return numValue > threshold ? 'high' : 'normal';
    }

    const lowMatch = referenceRange.match(/<\s*(\d+\.?\d*)/);
    if (lowMatch) {
      const threshold = parseFloat(lowMatch[1]);
      return numValue < threshold ? 'low' : 'normal';
    }

    return 'no_data';
  }

  /**
   * Determine status for microbiology results using the flag provided by the lab
   * We use the lab's flag directly without calculating our own interpretation
   */
  private determineMicrobiologyStatus(value: any, flag: string | null | undefined): 'normal' | 'abnormal' | 'no_data' {
    // Use the flag provided by the lab directly
    if (!flag || flag === null || flag === undefined || flag.trim() === '') {
      // No flag provided - default to normal if there's a value, otherwise no_data
      return value && String(value).trim() !== '' ? 'normal' : 'no_data';
    }

    // If lab provided a flag, it indicates an abnormal/positive finding
    // Common microbiology flags: "**", "*", or other flag values
    const flagTrimmed = flag.trim();
    if (flagTrimmed !== '') {
      return 'abnormal';
    }

    return 'normal';
  }

  /**
   * Extract comment text from test comment object
   * Similar to lab-results-dialog component
   */
  private getTestCommentText(comment: any): string {
    if (!comment) return '';
    
    // Handle both single Localization object and array of Localization objects
    if (comment.Localization) {
      // Single Localization object
      if (Array.isArray(comment.Localization)) {
        // Array of Localization objects - find English or default
        const enLocalization = comment.Localization.find((loc: any) => 
          loc.language === 'en' || loc.default === 'true'
        );
        return enLocalization?.Text || '';
      } else {
        // Single Localization object
        return comment.Localization.Text || '';
      }
    }
    
    return '';
  }


  private getTestType(testName: string): string {
    const name = testName.toLowerCase();
    
    if (name.includes('glucose') || name.includes('sugar')) return 'glucose';
    if (name.includes('cholesterol') || name.includes('lipid')) return 'lipid';
    if (name.includes('hemoglobin') || name.includes('hgb') || name.includes('hb')) return 'hemoglobin';
    if (name.includes('creatinine') || name.includes('kidney')) return 'kidney';
    if (name.includes('liver') || name.includes('alt') || name.includes('ast')) return 'liver';
    if (name.includes('thyroid') || name.includes('tsh') || name.includes('t4')) return 'thyroid';
    if (name.includes('vitamin') || name.includes('vit')) return 'vitamins';
    
    return 'other';
  }

  onDateRangeChange(event: any): void {
    this.selectedDateRange = event.value;
    
    // Always populate the custom date fields with the current range
    const { startDate, endDate } = this.getDateRange();
    this.customStartDate = moment.utc(startDate).startOf('day');
    this.customEndDate = moment.utc(endDate).endOf('day');
    
    this.applyFilters();
  }

  onCustomDateChange(): void {
    if (this.selectedDateRange === 'custom' && this.customStartDate && this.customEndDate) {
      this.applyFilters();
    }
  }

  onViewModeChange(): void {
    // View mode changed, no need to refilter data
    this.generateCollectionDates();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onSearchChange();
  }

  private applyFilters(): void {
    let filtered = [...this.monitoringData];

    // Filter by search query
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => 
        item.testName.toLowerCase().includes(query) ||
        item.testCode.toLowerCase().includes(query)
      );
    }

    // Filter by date range
    const { startDate, endDate } = this.getDateRange();
    const startMoment = moment.utc(startDate).startOf('day');
    const endMoment = moment.utc(endDate).endOf('day');
    
    filtered = filtered.filter(item => {
      const itemDate = moment.utc(item.latestDate);
      return itemDate.isBetween(startMoment, endMoment, null, '[]');
    });

    // Sort by alert severity (furthest out of range first)
    filtered.sort((a, b) => {
      // First sort by status priority: critical > abnormal > high/low > normal
      const statusPriority = { 'critical': 4, 'abnormal': 3, 'high': 2, 'low': 2, 'normal': 1 };
      const aPriority = statusPriority[a.status as keyof typeof statusPriority] || 0;
      const bPriority = statusPriority[b.status as keyof typeof statusPriority] || 0;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
      
      // If same status, sort by how far out of range (if applicable)
      if (a.status !== 'normal' && b.status !== 'normal') {
        const aDeviation = this.calculateDeviationFromRange(a);
        const bDeviation = this.calculateDeviationFromRange(b);
        return bDeviation - aDeviation; // Higher deviation first
      }
      
      // Finally sort by test name alphabetically
      return a.testName.localeCompare(b.testName);
    });

    this.filteredMonitoringData = filtered;
    this.generateCollectionDates();
    this.precomputeTableData();
    
    // Clean up comparison selection - remove any microbiology or non-numeric tests
    this.selectedForComparison = this.selectedForComparison.filter(testCode => {
      const item = this.filteredMonitoringData.find(i => i.testCode === testCode);
      return item && !item.isMicrobiology && this.hasNumericValues(item);
    });
    
    this.generateComparisonChartData();
  }

  private getDateRange(): { startDate: string; endDate: string } {
    let startDate: moment.Moment;
    let endDate: moment.Moment;

    switch (this.selectedDateRange) {
      case 'today':
        startDate = moment().utc().startOf('day');
        endDate = moment().utc().endOf('day');
        break;
      case 'week':
        startDate = moment().utc().startOf('week');
        endDate = moment().utc().endOf('week');
        break;
      case 'month':
        startDate = moment().utc().startOf('month');
        endDate = moment().utc().endOf('month');
        break;
      case 'year':
        startDate = moment().utc().startOf('year');
        endDate = moment().utc().endOf('year');
        break;
      case 'custom':
        if (this.customStartDate && this.customEndDate) {
          startDate = moment.utc(this.customStartDate).startOf('day');
          endDate = moment.utc(this.customEndDate).endOf('day');
        } else {
          startDate = moment().utc().startOf('month');
          endDate = moment().utc().endOf('month');
        }
        break;
      default:
        startDate = moment().utc().startOf('month');
        endDate = moment().utc().endOf('month');
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }

  private generateCollectionDates(): void {
    // Extract all unique collection dates from the filtered data
    const dates = new Set<string>();
    
    this.filteredMonitoringData.forEach(item => {
      item.history.forEach(historyItem => {
        dates.add(historyItem.date);
      });
    });

    // Convert to Date objects and sort
    this.collectionDates = Array.from(dates)
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => b.getTime() - a.getTime()); // Most recent first
  }

  private precomputeTableData(): void {
    // Pre-compute date keys
    this.precomputedDateKeys = this.collectionDates.map(date => moment(date).format('YYYY-MM-DD'));

    this.precomputedTableData = this.filteredMonitoringData.map(item => {
      const rowData: any = {
        testCode: item.testCode,
        testName: item.testName,
        referenceRange: item.referenceRange,
        unit: item.unit,
        status: item.status,
        isMicrobiology: item.isMicrobiology || false,
        valuesByDate: {}
      };

      // Pre-compute values for each collection date
      this.collectionDates.forEach(date => {
        const dateStr = moment(date).format('YYYY-MM-DD');
        const historyItem = item.history.find(h => moment(h.date).format('YYYY-MM-DD') === dateStr);
        
        const hasComment = historyItem && historyItem.comment && historyItem.comment.trim() !== '';
        const hasValue = historyItem && historyItem.value && String(historyItem.value).trim() !== '';
        
        rowData.valuesByDate[dateStr] = {
          value: historyItem ? String(historyItem.value) : '',
          comment: historyItem ? (historyItem.comment || '') : '',
          status: historyItem ? historyItem.status : 'empty',
          hasValue: hasValue,
          hasComment: hasComment,
          isCommentOnly: hasComment && !hasValue // For comment-only entries (like NGPOS procedures)
        };
      });

      return rowData;
    });
  }

  generateComparisonChartData(): void {
    if (this.selectedForComparison.length === 0) {
      this.comparisonChartData = null;
      this.lineChartData = [];
      this.yAxisLabel = 'Value';
      return;
    }

    // Filter out microbiology results and non-numeric tests from comparison
    const selectedItems = this.filteredMonitoringData.filter(item => 
      this.selectedForComparison.includes(item.testCode) && 
      !item.isMicrobiology && 
      this.hasNumericValues(item)
    );

    if (selectedItems.length === 0) {
      this.comparisonChartData = null;
      this.lineChartData = [];
      this.yAxisLabel = 'Value';
      return;
    }

    // Get all unique dates from selected items and ensure they're properly sorted
    const allDates = new Set<string>();
    selectedItems.forEach(item => {
      item.history.forEach(historyItem => {
        allDates.add(historyItem.date);
      });
    });

    // Convert to moment objects, sort chronologically (oldest to newest), then back to Date objects
    const sortedDates = Array.from(allDates)
      .map(dateStr => moment.utc(dateStr))
      .sort((a, b) => a.valueOf() - b.valueOf())
      .map(m => m.toDate());

    // Determine Y-axis label (use unit if all selected share the same one)
    const uniqueUnits = Array.from(new Set(selectedItems.map(i => i.unit).filter(u => !!u)));
    this.yAxisLabel = uniqueUnits.length === 1 ? String(uniqueUnits[0]) : 'Value';

    // Generate chart data for ngx-charts
    // CRITICAL: All series must use the EXACT same x-axis points in the EXACT same order
    // We create a consistent set of x-axis labels that will be used by ALL series
    // Use ISO date format in name so it sorts correctly alphabetically (YYYY-MM-DD)
    // Then use xAxisFormatting to display as "MMM DD"
    const xAxisLabels = sortedDates.map(date => ({
      timestamp: date.getTime(),
      displayName: moment.utc(date).format('MMM DD'),
      // Use ISO format (YYYY-MM-DD) so it sorts correctly when ngx-charts sorts alphabetically
      sortableName: moment.utc(date).format('YYYY-MM-DD'),
      isoDate: moment.utc(date).format('YYYY-MM-DD')
    }));

    // Generate chart data - ALL series MUST have data points for ALL dates in the SAME order
    // Filter out null values after creating all points to maintain consistent x-axis structure
    // ngx-charts doesn't handle null values well, so we filter them out but maintain order
    this.lineChartData = selectedItems.map(item => {
      // Create series data points for ALL dates in chronological order
      // Map over xAxisLabels to ensure all series use the same date structure
      const series = xAxisLabels
        .map((xAxisLabel, index) => {
          const historyItem = item.history.find(h => {
            const hDateStr = moment.utc(h.date).format('YYYY-MM-DD');
            return hDateStr === xAxisLabel.isoDate;
          });
          
          const value = historyItem ? parseFloat(String(historyItem.value)) : null;
          
          // Return the point with the value (null if no data for this date)
          // Include original index to maintain order after filtering
          return {
            name: xAxisLabel.sortableName, // Use ISO format (YYYY-MM-DD) for correct alphabetical sorting
            value: value, // Can be null - we'll filter these out
            originalIndex: index // Track original position to maintain order
          };
        })
        // Filter out null values (ngx-charts doesn't handle nulls well)
        .filter(point => point.value !== null)
        // Sort by original index to maintain chronological order after filtering
        .sort((a, b) => a.originalIndex - b.originalIndex)
        // Remove the originalIndex before returning
        .map(({ originalIndex, ...point }) => ({
          name: point.name,
          value: point.value as number // Type assertion since we filtered out nulls
        }));

      return {
        name: `${item.testName} (${item.unit})`,
        series: series
      };
    });

    // Keep the old format for compatibility
    const chartData = selectedItems.map(item => {
      const series = sortedDates.map(date => {
        const dateStr = moment.utc(date).format('YYYY-MM-DD');
        const historyItem = item.history.find(h => {
          const hDateStr = moment.utc(h.date).format('YYYY-MM-DD');
          return hDateStr === dateStr;
        });
        
        return {
          date: date,
          value: historyItem ? parseFloat(String(historyItem.value)) : null,
          status: historyItem ? historyItem.status : 'normal'
        };
      });

      return {
        name: item.testName,
        testCode: item.testCode,
        unit: item.unit,
        data: series.filter(point => point.value !== null)
      };
    });

    const allValues = chartData.flatMap(s => s.data.map(d => d.value)).filter(v => v !== null) as number[];
    this.comparisonChartData = {
      series: chartData,
      dates: sortedDates,
      maxValue: allValues.length > 0 ? Math.max(...allValues) : 0,
      minValue: allValues.length > 0 ? Math.min(...allValues) : 0
    };
  }

  private calculateDeviationFromRange(item: MonitoringData): number {
    // Calculate how far the value is from the normal range
    if (!item.referenceRange || item.status === 'normal') {
      return 0;
    }

    const value = parseFloat(String(item.latestValue));
    if (isNaN(value)) return 0;

    // Parse reference range (assuming format like "0.0 - 0.5" or "10-20")
    const rangeMatch = item.referenceRange.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
    if (!rangeMatch) return 0;

    const minRange = parseFloat(rangeMatch[1]);
    const maxRange = parseFloat(rangeMatch[2]);

    if (value < minRange) {
      return (minRange - value) / minRange; // Percentage below minimum
    } else if (value > maxRange) {
      return (value - maxRange) / maxRange; // Percentage above maximum
    }

    return 0; // Within range
  }


  getStatusColor(status: string): string {
    switch (status) {
      case 'normal': return 'green';
      case 'high': return 'orange';
      case 'low': return 'blue';
      case 'critical': return 'red';
      case 'abnormal': return 'orange'; // Microbiology abnormal findings
      default: return 'grey';
    }
  }

  getTrendIcon(trend: string): string {
    switch (trend) {
      case 'increasing': return 'trending_up';
      case 'decreasing': return 'trending_down';
      case 'stable': return 'trending_flat';
      default: return 'help_outline';
    }
  }

  getTrendColor(trend: string): string {
    switch (trend) {
      case 'increasing': return 'red';
      case 'decreasing': return 'blue';
      case 'stable': return 'green';
      default: return 'grey';
    }
  }

  formatDate(date: string): string {
    return moment(date).format('MMM DD, YYYY');
  }

  formatDateTime(date: string): string {
    return moment(date).format('MMM DD, YYYY HH:mm');
  }

  // Trend tracking methods
  selectTest(item: MonitoringData): void {
    this.selectedTest = item.testCode;
    this.selectedTestData = item;
    this.computedTrendData = this.precomputeTrendData(item);
    
    // Auto-scroll to trend detail view after a short delay to allow DOM update
    setTimeout(() => {
      const trendDetailElement = document.querySelector('.trend-detail-view');
      if (trendDetailElement) {
        trendDetailElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  }

  private precomputeTrendData(item: MonitoringData): any {
    const history = item.history || [];
    const isMicrobiology = item.isMicrobiology || false;
    
    const computedHistory = history.map((historyItem: any, index: number) => {
      // Only calculate change for non-microbiology results
      const changeData = !isMicrobiology 
        ? this.calculateChangeData(historyItem, history, index)
        : { changeClass: '', changeText: '', changeIcon: '', hasChange: false };
      
      // Format date and time properly
      const formattedDateTime = this.formatDateTime(historyItem.date);
      const formattedTime = moment(historyItem.date).format('HH:mm');
      
      return {
        ...historyItem,
        formattedDate: this.formatDate(historyItem.date),
        formattedDateTime: formattedDateTime,
        formattedTime: formattedTime,
        formattedValue: this.formatValue(historyItem.value, item.unit),
        changeClass: changeData.changeClass,
        changeText: changeData.changeText,
        changeIcon: changeData.changeIcon,
        hasChange: changeData.hasChange
      };
    });

    // Prepare ngx-charts line chart data (only for numeric/non-microbiology results)
    if (!isMicrobiology) {
      const sortedHistory = [...history].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      this.trendLineChartData = [{
        name: `${item.testName} (${item.unit})`,
        series: sortedHistory.map(historyItem => ({
          name: moment(historyItem.date).format('MMM DD'),
          value: parseFloat(String(historyItem.value)) || 0
        }))
      }];
    } else {
      // Empty chart data for microbiology
      this.trendLineChartData = [];
    }

    return {
      currentValue: this.formatValue(item.latestValue, item.unit),
      statusText: this.formatStatusText(item.status),
      statusIcon: this.getStatusIcon(item.status),
      trendText: isMicrobiology ? 'N/A' : this.formatTrendText(item.trend),
      trendIcon: isMicrobiology ? 'help_outline' : this.getTrendIcon(item.trend),
      trendPercentage: item.trendPercentage,
      dataPointsCount: history.length,
      referenceRange: item.referenceRange,
      timeRangeLabel: this.getDateRangeLabel(),
      history: computedHistory,
      chartData: !isMicrobiology ? this.prepareChartData(history, item.unit) : null,
      unit: item.unit
    };
  }

  private calculateChangeData(currentItem: any, history: any[], currentIndex: number): any {
    // History is sorted most recent first (index 0 = most recent, last index = oldest)
    // The oldest item (last index) should not have a change value
    // All other items should show change compared to the next older item
    
    // For the oldest item (last index), no change calculation
    if (currentIndex === history.length - 1) {
      return {
        changeClass: '',
        changeText: '',
        changeIcon: '',
        hasChange: false
      };
    }

    // Compare current item with the next older item (index + 1)
    const olderItem = history[currentIndex + 1];
    const changeClass = this.getChangeClass(currentItem.value, olderItem.value);
    const changeText = this.getChangeText(currentItem.value, olderItem.value);
    
    return {
      changeClass,
      changeText,
      changeIcon: this.getChangeIcon(changeClass),
      hasChange: true
    };
  }

  private formatValue(value: any, unit: string): string {
    if (typeof value === 'number') {
      return `${value} ${unit}`;
    }
    return `${value} ${unit}`;
  }

  private formatStatusText(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  private getStatusIcon(status: string): string {
    switch (status) {
      case 'normal': return 'check_circle';
      case 'critical': return 'error';
      case 'abnormal': return 'warning'; // Microbiology abnormal findings
      case 'high':
      case 'low': return 'warning';
      default: return 'help_outline';
    }
  }

  private formatTrendText(trend: string): string {
    switch (trend) {
      case 'increasing': return 'Rising';
      case 'decreasing': return 'Falling';
      case 'stable': return 'Stable';
      default: return 'No Data';
    }
  }

  private getChangeIcon(changeClass: string): string {
    switch (changeClass) {
      case 'increase': return 'arrow_upward';
      case 'decrease': return 'arrow_downward';
      case 'no-change': return 'remove';
      default: return 'remove';
    }
  }

  private prepareChartData(history: any[], unit: string): any {
    // Prepare data for chart visualization
    const chartData = history.map((item, index) => ({
      name: this.formatDate(item.date),
      value: parseFloat(item.value) || 0,
      date: new Date(item.date),
      status: item.status
    })).reverse(); // Reverse to show chronological order

    return {
      data: chartData,
      unit: unit,
      xAxisLabel: 'Date',
      yAxisLabel: `Value (${unit})`
    };
  }

  closeTrendView(): void {
    this.selectedTest = null;
    this.selectedTestData = null;
    this.computedTrendData = null;
    this.trendLineChartData = [];
  }

  // Table view helper methods
  getCollectionDates(): Date[] {
    return this.collectionDates;
  }

  formatTableDate(date: Date): string {
    const momentDate = moment(date);
    const today = moment();
    const daysDiff = today.diff(momentDate, 'days');
    
    if (daysDiff === 0) {
      return 'Today';
    } else if (daysDiff === 1) {
      return 'Yesterday';
    } else if (daysDiff < 7) {
      return momentDate.format('ddd, MMM D');
    } else {
      return momentDate.format('MMM D, YYYY');
    }
  }

  formatTableTime(date: Date): string {
    return moment(date).format('HH:mm');
  }

  getValueForDate(item: MonitoringData, date: Date): string {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const historyItem = item.history.find(h => moment(h.date).format('YYYY-MM-DD') === dateStr);
    return historyItem ? String(historyItem.value) : '';
  }

  getValueStatus(item: MonitoringData, date: Date): string {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const historyItem = item.history.find(h => moment(h.date).format('YYYY-MM-DD') === dateStr);
    return historyItem ? historyItem.status : 'normal';
  }

  getDateRangeLabel(): string {
    const chip = this.dateRangeChips.find(c => c.value === this.selectedDateRange);
    return chip ? chip.label : 'This Month';
  }

  // Comparison mode methods
  toggleComparisonSelection(testCode: string): void {
    // Find the test item
    const testItem = this.filteredMonitoringData.find(item => item.testCode === testCode);
    
    // Only allow selection of numeric/non-microbiology tests
    if (testItem && (testItem.isMicrobiology || !this.hasNumericValues(testItem))) {
      return; // Don't allow selection of microbiology or non-numeric tests
    }
    
    const index = this.selectedForComparison.indexOf(testCode);
    if (index > -1) {
      this.selectedForComparison.splice(index, 1);
    } else {
      this.selectedForComparison.push(testCode);
    }
    
    this.generateComparisonChartData();
    this.cdr.detectChanges();
  }

  isSelectedForComparison(testCode: string): boolean {
    return this.selectedForComparison.includes(testCode);
  }


  onTableRowClick(testCode: string): void {
    const item = this.filteredMonitoringData.find(item => item.testCode === testCode);
    if (item) {
      this.selectTest(item);
    }
  }

  getChangeClass(currentValue: number | string, previousValue: number | string): string {
    if (!this.isNumericValue(currentValue) || !this.isNumericValue(previousValue)) {
      return 'no-change';
    }
    
    const current = parseFloat(currentValue.toString());
    const previous = parseFloat(previousValue.toString());
    const change = ((current - previous) / previous) * 100;
    
    if (change > 0) return 'increase';
    if (change < 0) return 'decrease';
    return 'no-change';
  }

  getChangeText(currentValue: number | string, previousValue: number | string): string {
    if (!this.isNumericValue(currentValue) || !this.isNumericValue(previousValue)) {
      return 'N/A';
    }
    
    const current = parseFloat(currentValue.toString());
    const previous = parseFloat(previousValue.toString());
    const change = ((current - previous) / previous) * 100;
    
    if (change > 0) return `+${change.toFixed(1)}%`;
    if (change < 0) return `${change.toFixed(1)}%`;
    return 'No change';
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}

